import {ResponsiveContent} from "../../../ResponsiveNavTabs";
import React, {useRef, useState} from "react";
import {useAlert, useAuth} from "../../../../context";
import {Link, useSearchParams} from "react-router-dom";
import {Button, Form, ListGroup, Offcanvas, Stack} from "react-bootstrap";
import {useLocalFilter2, useSelect, useLongPress, useTag, useTagCount, useTagList, useTagOrderOptions, useTouch} from "../../../../hooks";
import {
    ItemLoading,
    MenuAdd,
    MenuBar,
    MenuClearSelectButton,
    MenuConfirmDeleteButton,
    MenuOrderBy,
    MenuSearchTerm,
    OurClearAlert,
    OurPagination2,
    PageTitle
} from "../../../_components";

export function Tags() {
    return <ResponsiveContent body={<TagsForm/>}/>
}


function TagsForm() {
    const {userId} = useAuth()
    const {success} = useAlert()

    // Get order options
    const {orderOptions, errorOrderOptions, clearErrorOrderOptions} = useTagOrderOptions()

    // API Actions
    const {deleteItem, errorValue, clearErrorValue} = useTag(userId)

    // API count all items
    const {count, setCount, loadingCount, errorCount, clearErrorCount} = useTagCount()

    // API items per page
    const {list, setList, loadingList, errorList, clearErrorList} = useTagList()

    // Filter
    const filteredList = useLocalFilter2(list, ["name"])

    // Select
    const [selList, toggleSelectItems, clearSelectItems, isItemSelected] = useSelect(list)


    function handleOnItemsDelete() {
        selList
            .forEach(item =>
                deleteItem(item.id)
                    .then(() => {
                        setCount(count => count - 1)
                        setList(prev => prev.filter(obj => obj.id !== item.id))
                        success(`${item.name} has been deleted!`)
                    })
                    .catch(err => console.warn(err.message))
            )
        clearSelectItems()
    }


    return <Stack>

        <OurClearAlert variant="danger" msg={errorOrderOptions} onClear={clearErrorOrderOptions}/>
        <OurClearAlert variant="danger" msg={errorCount} onClear={clearErrorCount}/>
        <OurClearAlert variant="danger" msg={errorList} onClear={clearErrorList}/>
        <OurClearAlert variant="danger" msg={errorValue} onClear={clearErrorValue}/>

        <PageTitle title="Tags">Manage {list.length} of {count} tags.</PageTitle>

        <MenuBar>
            <MenuAdd to="/backend/tag/add" icon="plus">Add</MenuAdd>
            <MenuSearchTerm/>
            <MenuOrderBy orderOptions={orderOptions}/>
            <MenuClearSelectButton hide={selList.length === 0} onClick={clearSelectItems}>
                {selList.length} {selList.length === 1 ? "row" : "rows"}
            </MenuClearSelectButton>
            <MenuConfirmDeleteButton hide={selList.length === 0} onSubmit={handleOnItemsDelete}>
                <span>{selList.length} {selList.length === 1 ? "tag" : "tags"}</span>
            </MenuConfirmDeleteButton>
            <FilterOffCanvas/>
        </MenuBar>

        <ItemLoading show={loadingCount || loadingList}/>

        {list && list.length > 0 ?
            <ListGroup variant="flush">
                {filteredList.map(item =>
                    <RenderItem key={item.id} item={item} selected={isItemSelected(item.id)} selectedCount={selList.length} onSelect={toggleSelectItems}
                                to={`/backend/tag/${item.id}`}/>
                )}
            </ListGroup>
            :
            <span className="text-center">No tag added yet</span>
        }

        <OurPagination2 loading={loadingCount || loadingList} itemCount={count}/>

    </Stack>
}

function RenderItem({item, selected, selectedCount, onSelect, to}) {
    const touch = useTouch()
    const [show, setShow] = useState(false)

    const ref = useRef(null)
    useLongPress(ref, () => onSelect(item.id))

    return (
        // <Stack direction="horizontal" gap="2" className={`mb-1 rounded ${selected && "bg-light"} ${show && "bg-light-subtle"}`}
        <Stack direction="horizontal" gap="2" className={`mb-1 rounded ${(selected || show) && "bg-light-subtle"}`}
               onMouseEnter={() => setShow(true)}
               onMouseLeave={() => setShow(false)}>
            <Stack ref={ref} direction="horizontal" className="flex-grow-1" gap="2">
                <div>
                    <i className={`bi bi-bookmark-fill fs-3 text-${item.variant}`}/>
                </div>
                <div>
                    <Stack direction="vertical" className="" gap={0}>
                        <Link to={to}
                              className="flex-shrink-0 link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                            <span className="text-wrap fw-semibold fs-4">{item.name}</span>
                        </Link>
                        {/*<span className="text-wrap fs-6 fw-light">{item.name}</span>*/}
                    </Stack>
                </div>
                <div className="ms-auto">
                    <Stack className="text-body-tertiary align-items-end justify-content-center" gap={0}>
                        <span className="text-nowrap fw-normal fs-6">Media: {item.media_count}</span>
                        <span className="text-nowrap fw-normal fs-6">Product: {item.product_count}</span>
                        <span className="text-nowrap fw-normal fs-6">Device: {item.device_count}</span>
                    </Stack>
                </div>
            </Stack>

            <div className="ms-auto">
                {(!touch || selected || selectedCount > 0) &&
                    <Button className="link-underline link-underline-opacity-0" variant="link" onClick={() => onSelect(item.id)}>
                        <span className={`bi ${selected ? "bi-check-square" : "bi-square"}`}></span>
                    </Button>
                }
            </div>
        </Stack>
    )
}

function FilterOffCanvas() {
    const [show, setShow] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams()

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    function handleSearchParams(checked, param) {
        setSearchParams(prev => {
            if (checked) {
                prev.delete(param)
            } else {
                prev.set(param, "1")
            }
            return prev
        }, {replace: true});

    }

    function handleAll(condition) {
        handleSearchParams(condition, "media")
        handleSearchParams(condition, "product")
        handleSearchParams(condition, "device")
        // handleClose()
    }

    return (
        <>
            <Button variant="primary" onClick={handleShow}>
                <span className="d-none d-sm-inline">Filter</span>
                <i className="bi bi-sliders2 d-sm-none"/>
            </Button>

            <Offcanvas show={show} onHide={handleClose} placement="end">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Filters</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>

                    <Form className="my-2">
                        <Form.Check
                            type="switch"
                            id="media-switch"
                            label="Media"
                            checked={!searchParams.get("media")}
                            onChange={e => handleSearchParams(e.target.checked, "media")}
                        />
                    </Form>

                    <Form className="my-2">
                        <Form.Check
                            type="switch"
                            id="product-switch"
                            label="Product"
                            checked={!searchParams.get("product")}
                            onChange={e => handleSearchParams(e.target.checked, "product")}
                        />
                    </Form>

                    <Form className="my-2">
                        <Form.Check
                            type="switch"
                            id="device-switch"
                            label="Device"
                            checked={!searchParams.get("device")}
                            onChange={e => handleSearchParams(e.target.checked, "device")}
                        />
                    </Form>

                    <div className="d-flex justify-content-start gap-2">
                        <Button variant="outline-secondary" className="my-2" onClick={() => handleAll(false)}>Exclude All</Button>
                        <Button variant="primary" className="my-2" onClick={() => handleAll(true)}>Include All</Button>
                    </div>

                </Offcanvas.Body>

            </Offcanvas>
        </>
    )
}